<template>
  <section class="user-review container">
    <div class="user-review__background"></div>
    <span class="user-review__avatars">
      <img
        v-for="i in 5"
        :key="i"
        :src="avatarImage(i)"
        alt=""
        class="user-review__avatar"
      />
    </span>
    <h2 class="billing-data__title">Over 1 million people love Shortform!</h2>

    <div class="user-review__buttons">
      <button class="user-review__button" @click="handleScroll('left')">
        <span class="user-review__button-icon iconfont ifaq"></span>
      </button>
      <button
        class="user-review__button user-review__button--right"
        @click="handleScroll('right')"
      >
        <span class="user-review__button-icon iconfont ifaq"></span>
      </button>
    </div>
    <div ref="container" class="user-review__content">
      <div v-for="item in items" :key="item.name" class="user-review__item">
        <div class="user-review__item-header">
          <div class="user-review__item-stars">
            <span
              v-for="i in 5"
              :key="i"
              class="user-review__item-star iconfont ifavorite"
            ></span>
          </div>
          <div class="user-review__item-date">{{ item.date }}</div>
        </div>
        <div class="user-review__item-text">
          <p>"{{ item.text }}"</p>
        </div>
        <div class="user-review__item-footer">
          <div class="user-review__item-name">{{ item.name }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'

const items = [
  {
    date: 'February 4, 2025',
    text: 'Shortform is a great tool to easily access litterature and information in general on topics that affect society and human curiosity on a whole. The summations are easily digestible and I really like how different perspectives from experts are shared on topics while you are reading.',
    name: 'Daniel F.',
  },
  {
    date: 'May 12, 2024',
    text: 'I’ve tried many book summary apps, but Shortform stands out as the best. The summaries go beyond just condensing content—they provide deeper insights, key takeaways, and even comparisons with other books, which helps me truly understand the concepts rather than just skim through them.',
    name: 'Teera Patsri',
  },
  {
    date: 'January 29, 2024',
    text: 'I appreciate that I can read AND listen to the Shortform books. At times I need both, so it is useful. I like that I can adjust the speed while listening. The titles are varied and interesting. Easy to navigate.',
    name: 'Gala P.',
  },
  {
    date: 'November 19, 2024',
    text: 'The one page and book summaries are superb, the support staff is very helpful in a timely way and the site is easy to navigate and there are lots of book summaries at Shortform.',
    name: 'Sandra Bartman',
  },
  {
    date: 'February 22, 2025',
    text: "Your book summaries strike the perfect balance between brevity and thoroughness. I like that you offer insights from other books or experts. I also like that you pose questions for reflection that enable me to implement what I've learned.",
    name: 'Steven G',
  },
  {
    date: 'March 27, 2024',
    text: "Completely transformed my daily walks! I used to just listen to music on my daily walks (which has its place) but now I've started using Shortform's audio feature, and can get exercise for my mind along with my body.",
    name: 'David D.',
  },
]

function avatarImage(index: number): string {
  return require(`@/assets.app/images/billing/user-review-${index}.jpeg`)
}

const container = ref<HTMLElement | null>(null)
const userInteracted = ref(false)

const handleScroll = (
  direction: 'left' | 'right',
  updateUserInteracted = true,
): void => {
  if (!container.value) {
    return
  }

  if (!userInteracted.value) {
    if (updateUserInteracted) {
      userInteracted.value = true
    }
    if (interval) {
      clearInterval(interval)
    }
  }

  const scrollWidth = 312 + 16 // Card width + gap
  const scrollAmount = scrollWidth * (direction === 'left' ? -1 : 1)
  container.value.scrollBy({
    left: scrollAmount,
    behavior: 'smooth',
  })
}

let interval: number | undefined = undefined

onMounted(() => {
  let scrollCount = 0
  interval = setInterval(() => {
    if (!userInteracted.value && scrollCount < items.length) {
      handleScroll('right', false)
      scrollCount++
    } else {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, 10000)
})

onUnmounted(() => {
  if (interval) {
    clearInterval(interval)
  }
})
</script>

<style scoped lang="scss">
.user-review {
  position: relative;

  &__avatars {
    display: flex;
    gap: 0px;
    justify-content: center;
    margin-bottom: 32px;
  }

  &__button {
    border-radius: 40px;
    border: 1.2px solid $c-border-7;
    background: $c-white;
    z-index: 1;
    position: absolute;
    top: 282px;
    left: calc(var(--container-padding) + 18px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3px 6px;
    transform: rotate(180deg);

    @include hover {
      border-color: $c-black-3;
    }

    @media (max-width: $w-max-content-width) {
      display: none;
    }

    &-icon {
      font-size: 24px;
    }

    &--right {
      left: auto;
      right: calc(var(--container-padding) + 18px);
      transform: rotate(0deg);
    }
  }

  &__background {
    position: absolute;
    top: 34px;
    left: 0;
    width: 100%;
    border-radius: 20px;
    background: $c-bg;
    height: 268px;
    z-index: -1;

    @media (max-width: $w-max-content-width) {
      left: var(--container-padding);
      width: calc(100% - calc(var(--container-padding) * 2));
    }
  }
  &__avatar {
    width: 68px;
    height: 68px;
    aspect-ratio: 1/1;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid $c-white;
    margin-right: -20px;
  }
  &__content {
    display: flex;
    flex-direction: row;
    gap: 16px;
    padding-bottom: 16px;
    margin-bottom: -16px;
    margin-top: 28px;
    margin-inline: 80px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }

    @media (max-width: $w-max-content-width) {
      @include full-width();
      padding-inline: calc(var(--container-padding) * 2);
    }
  }
  &__item {
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-radius: 8px;
    border: 1px solid $c-border-7;
    background: $c-white;
    box-shadow:
      0px 10px 10px 0px rgba(20, 28, 49, 0.03),
      0px 2px 6px 0px rgba(0, 0, 0, 0.03);
    padding: 24px 16px;
    width: 312px;
    height: fit-content;
    flex-shrink: 0;
    scroll-snap-align: center;
  }
  &__item-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  &__item-stars {
    display: flex;
    flex-direction: row;
    gap: 4px;
  }
  &__item-star {
    color: $c-yellow;
    width: 20px;
    height: 20px;
  }
  &__item-date {
    color: $c-black-6;
    text-align: right;
    font-size: 12px;
    line-height: 24px;
  }
  &__item-text {
    color: $c-black-15;
    font-size: 14px;
    line-height: 20px;
  }
  &__item-name {
    color: $c-black-15;
    font-size: 14px;
    line-height: 22px;
    font-weight: 500;
  }
}

.night-mode {
  .user-review {
    &__avatar {
      border-color: $c-dark-3;
    }
    &__item {
      background: $c-black-7;
      border: 1px solid $c-black-15;
    }
    &__item-date {
      color: $c-black-9;
    }
    &__item-text {
      p {
        color: $c-black-11;
      }
    }
    &__item-name {
      color: $c-black-11;
    }

    &__button {
      border-color: $c-black-15;
      background: $c-black-7;
      color: $c-white;
    }

    &__background {
      background: $c-black-8;
    }
  }
}
</style>
